/** Campaign statuses as returned by the API, with display label and chip color. */
export const CAMPAIGN_STATUS = {
  draft: { label: 'Draft', color: 'grey' },
  active: { label: 'Active', color: 'success' },
  paused: { label: 'Paused', color: 'warning' },
  completed: { label: 'Completed', color: 'info' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

/** Number statuses; "called" means the callee answered. */
export const NUMBER_STATUS = {
  pending: { label: 'Pending', color: 'grey' },
  dialing: { label: 'Dialing', color: 'info' },
  called: { label: 'Called', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'blue-grey' },
};

export const NUMBER_STATUS_ORDER = ['pending', 'dialing', 'called', 'failed', 'cancelled'];

/** Campaign limits enforced by the API. */
export const CAMPAIGN_LIMITS = {
  interval: { min: 5, max: 86400, default: 60 },
  batch: { min: 1, max: 100, default: 1 },
  attempts: { min: 1, max: 10, default: 1 },
  retryDelay: { min: 30, max: 604800, default: 300 },
  csvMaxBytes: 5 * 1024 * 1024,
};

/** Auto-refresh period of campaign screens while a campaign is running. */
export const CAMPAIGN_REFRESH_MS = 10000;

export function campaignStatus(status) {
  return CAMPAIGN_STATUS[status] || { label: status || '—', color: 'grey' };
}

export function numberStatus(status) {
  return NUMBER_STATUS[status] || { label: status || '—', color: 'grey' };
}

/** Numbers that have reached a final state (called, failed or cancelled). */
export function finishedCount(campaign) {
  const c = campaign?.number_counts || {};
  return (c.called || 0) + (c.failed || 0) + (c.cancelled || 0);
}

/** Percentage of numbers that reached a final state. */
export function progressPercent(campaign) {
  const total = campaign?.total_numbers || 0;
  if (!total) return 0;
  return Math.round((finishedCount(campaign) / total) * 100);
}

export function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

/** ISO timestamp -> value for <input type="datetime-local"> (local time, minutes precision). */
export function toDateTimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value -> RFC 3339 timestamp, or null when empty. */
export function fromDateTimeLocal(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Human-readable duration for interval / retry settings, e.g. 90 -> "1 min 30 s". */
export function formatSeconds(total) {
  const s = Number(total) || 0;
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  return [h ? `${h} h` : '', m ? `${m} min` : '', rest ? `${rest} s` : ''].filter(Boolean).join(' ');
}

/** Splits pasted text into numbers: one per line, or separated by commas / semicolons. */
export function parsePastedNumbers(raw) {
  return String(raw || '')
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((phone) => ({ phone_number: phone }));
}

/** Downloads an example CSV showing the accepted columns. */
export function downloadCsvTemplate() {
  const content = 'phone_number,name,note\n+905551234567,Ayşe Yılmaz,Renewal due in October\n+905557654321,Mehmet Demir,\n';
  // BOM so spreadsheet tools open non-ASCII names correctly.
  const blob = new Blob([`\ufeff${content}`], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'campaign-numbers-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/** Statuses the axios interceptor already reports with its own toast. */
const GLOBALLY_NOTIFIED = [401, 403, 404, 422, 500];

/** Shows an error toast unless the global axios interceptor already did. */
export function notifyError(store, err, fallback) {
  const status = err?.response?.status;
  if (GLOBALLY_NOTIFIED.includes(status)) return;
  store.dispatch('ui/showNotification', {
    type: 'error',
    message: err?.response?.data?.message || fallback,
    timeout: 6000,
  });
}

export function notifySuccess(store, message) {
  store.dispatch('ui/showNotification', { type: 'success', message, timeout: 4000 });
}
